/* ══════════════════════════════════════════════════════════
   Store Locator — nearest-store resolver
   ──────────────────────────────────────────────────────────
   Data flow:
     1. On load, fetch stores.json from Shopify Files (URL comes
        from the section's data-sl-data-url attribute).
     2. If that fetch fails OR returns invalid JSON, fall back to
        FLAGSHIP_FALLBACKS (hardcoded below) so the section never
        renders blank.
     3. IP-geolocate the user (no permission prompt).
     4. Compute nearest store via haversine; if <=50mi render it,
        otherwise render the flagship with out-of-region copy.

   stores.json shape:
     {
       "updated": "2026-04-19",
       "brandPhotos": {
         "golf-galaxy":  { "url": "https://cdn.shopify.com/...", "alt": "..." },
         "dicks":        { "url": "https://cdn.shopify.com/...", "alt": "..." },
         "pga-tour-superstore": { "url": "https://cdn.shopify.com/...", "alt": "..." }
       },
       "stores": [
         { "id": "gg-tysons", "retailer": "golf-galaxy",
           "name": "Golf Galaxy — Tysons Corner",
           "city": "McLean", "state": "VA",
           "lat": 38.9176, "lng": -77.2236 },
         ...
       ]
     }
   ══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ── Safety net: 20 flagship stores, hardcoded ─────────────
  // Used only if the remote stores.json fetch fails. Keeps the
  // section alive and useful even under worst-case conditions.
  const FLAGSHIP_FALLBACKS = {
    brandPhotos: {
      'golf-galaxy':         { url: '{{BRAND_PHOTO_GOLF_GALAXY}}',  alt: 'Golf Galaxy storefront' },
      'dicks':               { url: '{{BRAND_PHOTO_DICKS}}',        alt: "Dick's House of Sport storefront" },
      'dicks-house-of-sport': { url: '{{BRAND_PHOTO_DICKS}}',        alt: "Dick's House of Sport storefront" },
      'pga-tour-superstore': { url: '{{BRAND_PHOTO_PGA}}',          alt: 'PGA Tour Superstore storefront' }
    },
    stores: [
      { id: 'gg-tysons',       retailer: 'golf-galaxy', name: 'Golf Galaxy — Tysons Corner',  city: 'McLean',        state: 'VA', lat: 38.9176, lng: -77.2236 },
      { id: 'gg-manhattan',    retailer: 'golf-galaxy', name: 'Golf Galaxy — Manhattan',       city: 'New York',      state: 'NY', lat: 40.7580, lng: -73.9855 },
      { id: 'gg-santa-monica', retailer: 'golf-galaxy', name: 'Golf Galaxy — Santa Monica',    city: 'Santa Monica',  state: 'CA', lat: 34.0195, lng: -118.4912 },
      { id: 'gg-dallas',       retailer: 'golf-galaxy', name: 'Golf Galaxy — Dallas',          city: 'Dallas',        state: 'TX', lat: 32.7767, lng: -96.7970 },
      { id: 'gg-chicago',      retailer: 'golf-galaxy', name: 'Golf Galaxy — Chicago',         city: 'Chicago',       state: 'IL', lat: 41.8781, lng: -87.6298 },
      { id: 'gg-denver',       retailer: 'golf-galaxy', name: 'Golf Galaxy — Denver',          city: 'Denver',        state: 'CO', lat: 39.7392, lng: -104.9903 },
      { id: 'gg-seattle',      retailer: 'golf-galaxy', name: 'Golf Galaxy — Seattle',         city: 'Seattle',       state: 'WA', lat: 47.6062, lng: -122.3321 },
      { id: 'gg-miami',        retailer: 'golf-galaxy', name: 'Golf Galaxy — Miami',           city: 'Miami',         state: 'FL', lat: 25.7617, lng: -80.1918 },
      { id: 'dicks-boston',    retailer: 'dicks-house-of-sport',       name: "Dick's House of Sport — Boston", city: 'Boston',       state: 'MA', lat: 42.3601, lng: -71.0589 },
      { id: 'dicks-minneapolis', retailer: 'dicks-house-of-sport',     name: "Dick's House of Sport — Minneapolis", city: 'Minneapolis', state: 'MN', lat: 44.9778, lng: -93.2650 },
      { id: 'dicks-phoenix',   retailer: 'dicks-house-of-sport',       name: "Dick's House of Sport — Phoenix", city: 'Phoenix',     state: 'AZ', lat: 33.4484, lng: -112.0740 },
      { id: 'dicks-philly',    retailer: 'dicks-house-of-sport',       name: "Dick's House of Sport — Philadelphia", city: 'Philadelphia', state: 'PA', lat: 39.9526, lng: -75.1652 },
      { id: 'dicks-nashville', retailer: 'dicks-house-of-sport',       name: "Dick's House of Sport — Nashville", city: 'Nashville', state: 'TN', lat: 36.1627, lng: -86.7816 },
      { id: 'dicks-orlando',   retailer: 'dicks-house-of-sport',       name: "Dick's House of Sport — Orlando", city: 'Orlando',     state: 'FL', lat: 28.5383, lng: -81.3792 },
      { id: 'pga-atlanta',     retailer: 'pga-tour-superstore', name: 'PGA Tour Superstore — Atlanta', city: 'Atlanta', state: 'GA', lat: 33.7490, lng: -84.3880 },
      { id: 'pga-houston',     retailer: 'pga-tour-superstore', name: 'PGA Tour Superstore — Houston', city: 'Houston', state: 'TX', lat: 29.7604, lng: -95.3698 },
      { id: 'pga-charlotte',   retailer: 'pga-tour-superstore', name: 'PGA Tour Superstore — Charlotte', city: 'Charlotte', state: 'NC', lat: 35.2271, lng: -80.8431 },
      { id: 'pga-columbus',    retailer: 'pga-tour-superstore', name: 'PGA Tour Superstore — Columbus', city: 'Columbus', state: 'OH', lat: 39.9612, lng: -82.9988 },
      { id: 'pga-kansas-city', retailer: 'pga-tour-superstore', name: 'PGA Tour Superstore — Kansas City', city: 'Kansas City', state: 'MO', lat: 39.0997, lng: -94.5786 },
      { id: 'pga-vegas',       retailer: 'pga-tour-superstore', name: 'PGA Tour Superstore — Las Vegas', city: 'Las Vegas', state: 'NV', lat: 36.1699, lng: -115.1398 }
    ]
  };

  // ── Constants ─────────────────────────────────────────────
  const MAX_NEARBY_MI = 50;
  const EARTH_RADIUS_MI = 3958.8;
  const FETCH_TIMEOUT_MS = 4000;

  // In-memory cache so multiple sections on the same page only fetch once
  let DATA_PROMISE = null;

  // ── Utils ─────────────────────────────────────────────────
  const toRad = (d) => (d * Math.PI) / 180;

  function haversineMi(lat1, lng1, lat2, lng2) {
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MI * Math.asin(Math.sqrt(a));
  }

  function findNearest(lat, lng, stores) {
    let best = null, bestDist = Infinity;
    for (const s of stores) {
      if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) continue;
      const d = haversineMi(lat, lng, s.lat, s.lng);
      if (d < bestDist) { best = s; bestDist = d; }
    }
    return best ? { store: best, distanceMi: bestDist } : null;
  }

  const findById = (id, stores) => stores.find((s) => s.id === id) || null;

  // ── Fetch store data with fallback ────────────────────────
  async function loadData(url) {
    if (DATA_PROMISE) return DATA_PROMISE;
    DATA_PROMISE = (async () => {
      if (!url) return FLAGSHIP_FALLBACKS;
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
        const res = await fetch(url, { signal: ctl.signal, cache: 'default' });
        clearTimeout(t);
        if (!res.ok) throw new Error('bad status: ' + res.status);
        const data = await res.json();
        if (!Array.isArray(data.stores) || data.stores.length === 0) {
          throw new Error('invalid stores payload');
        }
        // Merge: any brandPhotos not in remote data fall back to hardcoded
        data.brandPhotos = Object.assign({}, FLAGSHIP_FALLBACKS.brandPhotos, data.brandPhotos || {});
        return data;
      } catch (err) {
        console.warn('[store-locator] Using hardcoded fallback:', err.message);
        return FLAGSHIP_FALLBACKS;
      }
    })();
    return DATA_PROMISE;
  }

  // ── IP geolocation (no permission prompt) ─────────────────
  // Tries two free providers in sequence for reliability. Neither
  // requires an API key. If you outgrow these, swap in a paid
  // service (ipinfo.io, MaxMind) or a Cloudflare Worker reading
  // CF's built-in cf.latitude/cf.longitude (free, most accurate).
  //
  // Free-tier limits (as of 2026):
  //   geojs.io:  No published limit, CORS enabled, no API key
  //   ipapi.co:  1,000 req/day per IP, CORS works but rate-limits hard
  async function ipGeolocate() {
    // Provider 1: geojs.io
    try {
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), 3000);
      const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: ctl.signal });
      clearTimeout(t);
      if (res.ok) {
        const d = await res.json();
        const lat = parseFloat(d.latitude), lng = parseFloat(d.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
      }
    } catch (e) {
      console.warn('[store-locator] geojs.io failed:', e.message);
    }

    // Provider 2: ipapi.co fallback
    try {
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), 3000);
      const res = await fetch('https://ipapi.co/json/', { signal: ctl.signal });
      clearTimeout(t);
      if (!res.ok) return null;
      const d = await res.json();
      const lat = parseFloat(d.latitude), lng = parseFloat(d.longitude);
      return (Number.isFinite(lat) && Number.isFinite(lng)) ? { lat, lng } : null;
    } catch (e) {
      console.warn('[store-locator] ipapi.co failed:', e.message);
      return null;
    }
  }

  // ── Render ────────────────────────────────────────────────
  function render(root, brandPhotos, opts) {
    const { store, distanceMi, outOfRegion } = opts;
    const card = root.querySelector('[data-sl-card]');
    const photoEl = root.querySelector('[data-sl-photo]');
    const nameEl = root.querySelector('[data-sl-name]');
    const distEl = root.querySelector('[data-sl-dist]');
    const subEl = root.querySelector('[data-sl-sub]');
    if (!store) return;

    const brand = brandPhotos[store.retailer];
    if (brand && brand.url && !brand.url.startsWith('{{')) {
      photoEl.src = brand.url;
      photoEl.alt = brand.alt || '';
      photoEl.addEventListener('load', () => {
        card.setAttribute('data-sl-has-photo', '');
      }, { once: true });
    }

    nameEl.textContent = store.name;

    if (outOfRegion) {
      card.setAttribute('data-sl-out-of-region', '');
      distEl.textContent = 'Not in your area? We ship nationwide.';
      if (subEl && !subEl.dataset.slOriginalSub) {
        subEl.dataset.slOriginalSub = subEl.textContent;
        subEl.textContent = "Looks like you're a ways from our retailers — shop online and we'll ship direct.";
      }
    } else if (Number.isFinite(distanceMi)) {
      distEl.innerHTML = `<strong>${distanceMi.toFixed(1)} mi away</strong> · ${store.city}, ${store.state}`;
    } else {
      distEl.innerHTML = `${store.city}, ${store.state}`;
    }

    card.removeAttribute('data-sl-loading');
  }

  // ── Init per section ──────────────────────────────────────
  async function init(root) {
    const section = root.closest('[data-sl-section]') || root.parentElement;
    const flagshipId = section?.dataset?.slFlagshipId;
    const dataUrl = section?.dataset?.slDataUrl;
    const card = root.querySelector('[data-sl-card]');
    card.setAttribute('data-sl-loading', '');

    const [data, coords] = await Promise.all([loadData(dataUrl), ipGeolocate()]);
    const { stores, brandPhotos } = data;
    const flagship = findById(flagshipId, stores) || stores[0];

    if (!coords) {
      render(root, brandPhotos, { store: flagship, distanceMi: null, outOfRegion: false });
      return;
    }
    const nearest = findNearest(coords.lat, coords.lng, stores);
    if (nearest && nearest.distanceMi <= MAX_NEARBY_MI) {
      render(root, brandPhotos, { store: nearest.store, distanceMi: nearest.distanceMi, outOfRegion: false });
    } else {
      render(root, brandPhotos, { store: flagship, distanceMi: null, outOfRegion: true });
    }
  }

  function bootAll() { document.querySelectorAll('[data-sl-root]').forEach(init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else { bootAll(); }

  document.addEventListener('shopify:section:load', (e) => {
    DATA_PROMISE = null;
    const root = e.target.querySelector('[data-sl-root]');
    if (root) init(root);
  });
})();
